<template>
  <div class="keberatan-panel">
    <div class="keberatan-toolbar">
      <label class="keberatan-search" for="keberatan-search-input">
        <span class="keberatan-search-icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="1.8" />
            <path d="M20 20l-3.5-3.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
          </svg>
        </span>
        <span class="sr-only">Cari keberatan</span>
        <input
          id="keberatan-search-input"
          v-model="search"
          type="search"
          placeholder="Cari NIP, jabatan, atau unit kerja…"
          autocomplete="off"
        />
      </label>

      <div class="keberatan-toolbar-right">
        <div class="keberatan-chips" role="group" aria-label="Filter status">
          <button
            type="button"
            class="keberatan-chip"
            :class="{ 'is-active': statusFilter === 'all' }"
            @click="statusFilter = 'all'"
          >
            Semua
          </button>
          <button
            type="button"
            class="keberatan-chip"
            :class="{ 'is-active': statusFilter === 'pending' }"
            @click="statusFilter = 'pending'"
          >
            Belum
          </button>
          <button
            type="button"
            class="keberatan-chip"
            :class="{ 'is-active': statusFilter === 'done' }"
            @click="statusFilter = 'done'"
          >
            Selesai
          </button>
        </div>
        <button
          type="button"
          class="keberatan-create-btn"
          :disabled="loadingPengaturan"
          @click="openCreate"
        >
          Buat keberatan
        </button>
        <div class="keberatan-meta" v-if="!loading">
          <span class="keberatan-count">{{ filteredItems.length }}</span>
          <span class="keberatan-count-label">data</span>
        </div>
      </div>
    </div>

    <div v-if="loading" class="keberatan-skeleton" aria-busy="true" aria-label="Memuat data">
      <div v-for="n in 5" :key="n" class="keberatan-skeleton-row">
        <span class="sk sk-lg"></span>
        <span class="sk sk-md"></span>
        <span class="sk sk-md"></span>
        <span class="sk sk-sm"></span>
        <span class="sk sk-xs"></span>
        <span class="sk sk-sm"></span>
      </div>
    </div>

    <div v-else-if="!filteredItems.length" class="keberatan-empty">
      <div class="keberatan-empty-mark" aria-hidden="true"></div>
      <p class="keberatan-empty-title">Belum ada keberatan</p>
      <p class="keberatan-empty-text">
        {{
          search || statusFilter !== "all"
            ? "Tidak ada data yang cocok dengan filter."
            : "Data keberatan akan muncul di sini."
        }}
      </p>
      <button
        v-if="!search && statusFilter === 'all'"
        type="button"
        class="keberatan-create-btn keberatan-create-btn--empty"
        :disabled="loadingPengaturan"
        @click="openCreate"
      >
        Buat keberatan
      </button>
    </div>

    <div v-else class="keberatan-table-wrap">
      <table class="keberatan-table">
        <thead>
          <tr>
            <th class="col-toggle"></th>
            <th>Pegawai</th>
            <th>Unit Kerja</th>
            <th>Atasan</th>
            <th>Triwulan</th>
            <th>Status</th>
            <th>Tanggal</th>
          </tr>
        </thead>
        <tbody v-for="item in pagedItems" :key="item.id">
          <tr
            class="keberatan-row"
            :class="{ 'is-open': expandedId === item.id }"
            @click="toggleExpand(item.id)"
          >
            <td class="col-toggle">
              <button
                type="button"
                class="keberatan-expand-btn"
                :aria-expanded="expandedId === item.id ? 'true' : 'false'"
                :aria-label="expandedId === item.id ? 'Tutup detail' : 'Lihat detail'"
                @click.stop="toggleExpand(item.id)"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M6 9l6 6 6-6"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </td>
            <td>
              <span class="cell-primary">{{ jabatanNama(item) }}</span>
              <span class="cell-sub mono">{{ item.nip }}</span>
            </td>
            <td>
              <span class="cell-muted">{{ unitKerja(item) }}</span>
            </td>
            <td>
              <span class="cell-primary">{{ jabatanAtasan(item) }}</span>
              <span class="cell-sub mono">{{ nipAtasan(item) }}</span>
            </td>
            <td>
              <span class="cell-muted">{{ formatTriwulan(item.triwulan) }}</span>
            </td>
            <td>
              <span
                class="status-pill"
                :class="item.is_completed ? 'is-done' : 'is-pending'"
              >
                {{ item.is_completed ? "Selesai" : "Belum" }}
              </span>
            </td>
            <td>
              <span class="cell-muted">{{ formatDate(item.created_at) }}</span>
            </td>
          </tr>
          <tr
            v-if="expandedId === item.id"
            class="keberatan-detail-row"
          >
            <td colspan="7">
              <div class="keberatan-detail">
                <div class="detail-block">
                  <span class="detail-label">Atasan penilai</span>
                  <span class="detail-value">{{ jabatanAtasanPenilai(item) }}</span>
                  <span class="detail-sub">{{ unitKerjaAtasanPenilai(item) }}</span>
                </div>
                <div class="detail-block">
                  <span class="detail-label">Unit kerja atasan</span>
                  <span class="detail-value">{{ unitKerjaAtasan(item) }}</span>
                </div>
                <div class="detail-block">
                  <span class="detail-label">Periode</span>
                  <span class="detail-value">{{ formatPeriode(item) }}</span>
                </div>
                <div class="detail-block detail-block--full">
                  <span class="detail-label">Alasan</span>
                  <span class="detail-value detail-value--text">{{ alasanText(item) }}</span>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <footer v-if="!loading && filteredItems.length > pageSize" class="keberatan-pager">
      <button type="button" :disabled="page <= 1" @click="page--">Sebelumnya</button>
      <span>{{ page }} / {{ totalPages }}</span>
      <button type="button" :disabled="page >= totalPages" @click="page++">Berikutnya</button>
    </footer>

    <div
      v-if="createOpen"
      class="keberatan-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="keberatan-create-title"
      @click.self="closeCreate"
    >
      <div class="keberatan-modal">
        <header class="keberatan-modal-header">
          <h2 id="keberatan-create-title">Buat keberatan</h2>
          <button
            type="button"
            class="keberatan-modal-close"
            aria-label="Tutup"
            :disabled="submitting"
            @click="closeCreate"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </header>

        <div class="keberatan-modal-body">
          <p v-if="pengaturanError" class="keberatan-form-error">{{ pengaturanError }}</p>

          <label class="keberatan-field">
            <span class="keberatan-field-label">Periode SKP</span>
            <select
              v-model="form.id_konfigurasi"
              :disabled="loadingPengaturan || submitting || !pengaturanOptions.length"
            >
              <option disabled :value="null">
                {{ loadingPengaturan ? "Memuat…" : "Pilih periode SKP" }}
              </option>
              <option
                v-for="opt in pengaturanOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </label>

          <label class="keberatan-field">
            <span class="keberatan-field-label">Triwulan</span>
            <select v-model="form.triwulan" :disabled="submitting">
              <option disabled value="">Pilih triwulan</option>
              <option
                v-for="opt in triwulanOptions"
                :key="String(opt.value)"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </label>

          <label class="keberatan-field">
            <span class="keberatan-field-label">Alasan</span>
            <textarea
              v-model.trim="form.alasan"
              rows="4"
              :disabled="submitting"
              placeholder="Tuliskan alasan keberatan…"
            ></textarea>
          </label>
        </div>

        <footer class="keberatan-modal-footer">
          <button
            type="button"
            class="keberatan-btn-secondary"
            :disabled="submitting"
            @click="closeCreate"
          >
            Batal
          </button>
          <button
            type="button"
            class="keberatan-create-btn"
            :disabled="!canSubmit"
            @click="submitCreate"
          >
            {{ submitting ? "Menyimpan…" : "Simpan" }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import store from "@/store";
import SKPService from "@/services/SKPService";

export default {
  name: "KeberatanKinerja",
  data() {
    return {
      loading: true,
      items: [],
      search: "",
      statusFilter: "all",
      expandedId: null,
      page: 1,
      pageSize: 10,
      createOpen: false,
      loadingPengaturan: false,
      pengaturanError: "",
      pengaturanOptions: [],
      submitting: false,
      form: {
        id_konfigurasi: null,
        triwulan: "",
        alasan: "",
      },
      triwulanOptions: [
        { value: 1, label: "TW 1" },
        { value: 2, label: "TW 2" },
        { value: 3, label: "TW 3" },
        { value: 4, label: "TW 4" },
        { value: "tahunan", label: "Tahunan" },
      ],
    };
  },
  computed: {
    canSubmit() {
      return (
        this.form.id_konfigurasi != null &&
        this.form.triwulan !== "" &&
        (this.form.alasan || "").trim() !== "" &&
        !this.submitting &&
        !this.loadingPengaturan
      );
    },
    filteredItems() {
      let list = this.items;

      if (this.statusFilter === "pending") {
        list = list.filter((item) => !item.is_completed);
      } else if (this.statusFilter === "done") {
        list = list.filter((item) => !!item.is_completed);
      }

      const q = (this.search || "").trim().toLowerCase();
      if (!q) return list;

      return list.filter((item) => {
        const hay = [
          item.nip,
          this.jabatanNama(item),
          this.unitKerja(item),
          this.jabatanAtasan(item),
          this.nipAtasan(item),
        ]
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredItems.length / this.pageSize));
    },
    pagedItems() {
      const start = (this.page - 1) * this.pageSize;
      return this.filteredItems.slice(start, start + this.pageSize);
    },
  },
  watch: {
    search() {
      this.page = 1;
      this.expandedId = null;
    },
    statusFilter() {
      this.page = 1;
      this.expandedId = null;
    },
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      SKPService.getKeberatan()
        .then((response) => {
          const payload = response && response.data ? response.data : {};
          this.items = Array.isArray(payload.data) ? payload.data : [];
        })
        .catch(() => {
          this.items = [];
          store.commit("snackbar/setSnack", {
            message: "Gagal memuat data keberatan. Coba beberapa saat lagi.",
            color: "error",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openCreate() {
      this.createOpen = true;
      this.pengaturanError = "";
      this.form = { id_konfigurasi: null, triwulan: "", alasan: "" };
      this.pengaturanOptions = [];
      this.loadingPengaturan = true;
      SKPService.getListDataPengaturanSkp()
        .then((response) => {
          const payload = response && response.data ? response.data : {};
          const list = Array.isArray(payload.data) ? payload.data : [];
          this.pengaturanOptions = list
            .map((item) => {
              const value = item.id != null ? item.id : item.id_konfigurasi;
              if (value == null) return null;
              const label =
                item.periode_skp ||
                [item.jabatan_nama, item.unit_kerja].filter(Boolean).join(" · ") ||
                "Konfigurasi " + value;
              return { value, label };
            })
            .filter(Boolean);
          if (!this.pengaturanOptions.length) {
            this.pengaturanError = "Tidak ada periode SKP yang tersedia.";
          }
        })
        .catch(() => {
          this.pengaturanError = "Gagal memuat daftar periode SKP.";
          store.commit("snackbar/setSnack", {
            message: "Gagal memuat daftar periode SKP. Coba beberapa saat lagi.",
            color: "error",
          });
        })
        .finally(() => {
          this.loadingPengaturan = false;
        });
    },
    closeCreate() {
      if (this.submitting) return;
      this.createOpen = false;
      this.pengaturanError = "";
      this.form = { id_konfigurasi: null, triwulan: "", alasan: "" };
    },
    submitCreate() {
      if (!this.canSubmit) return;
      this.submitting = true;
      const triwulan =
        this.form.triwulan === "tahunan" ? null : Number(this.form.triwulan);
      SKPService.createKeberatan({
        id_konfigurasi: this.form.id_konfigurasi,
        triwulan,
        alasan: (this.form.alasan || "").trim(),
      })
        .then((response) => {
          const payload = response && response.data ? response.data : {};
          const message =
            payload.message || "Keberatan berhasil dibuat.";
          store.commit("snackbar/setSnack", {
            message,
            color: "success",
          });
          this.submitting = false;
          this.createOpen = false;
          this.form = { id_konfigurasi: null, triwulan: "", alasan: "" };
          this.load();
        })
        .catch((error) => {
          const msg =
            (error &&
              error.response &&
              error.response.data &&
              error.response.data.message) ||
            "Gagal membuat keberatan. Coba beberapa saat lagi.";
          store.commit("snackbar/setSnack", {
            message: msg,
            color: "error",
          });
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    toggleExpand(id) {
      this.expandedId = this.expandedId === id ? null : id;
    },
    cfg(item) {
      return (item && item.konfigurasi) || {};
    },
    jabatanNama(item) {
      return this.cfg(item).jabatan_nama || "—";
    },
    unitKerja(item) {
      return this.cfg(item).unit_kerja || "—";
    },
    jabatanAtasan(item) {
      return this.cfg(item).jabatan_atasan || "—";
    },
    nipAtasan(item) {
      return this.cfg(item).nip_atasan || "";
    },
    jabatanAtasanPenilai(item) {
      return this.cfg(item).jabatan_atasan_penilai || "—";
    },
    unitKerjaAtasan(item) {
      return this.cfg(item).unit_kerja_atasan || "—";
    },
    unitKerjaAtasanPenilai(item) {
      return this.cfg(item).unit_kerja_atasan_penilai || "";
    },
    alasanText(item) {
      return (item && item.alasan) || "—";
    },
    formatTriwulan(value) {
      if (value === null || value === undefined || value === "") return "Tahunan";
      return "TW " + value;
    },
    formatPeriode(item) {
      const c = this.cfg(item);
      if (c.periode_awal == null || c.periode_akhir == null) return "—";
      return "Bulan " + c.periode_awal + " – " + c.periode_akhir;
    },
    formatDate(value) {
      if (!value) return "—";
      const d = new Date(String(value).replace(" ", "T"));
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
  },
};
</script>

<style scoped>
.keberatan-panel {
  --kb-ink: #16322b;
  --kb-muted: #5f736c;
  --kb-line: rgba(22, 50, 43, 0.1);
  --kb-surface: #f7faf8;
  --kb-accent: #1f7a5c;
  --kb-accent-soft: #d7efe4;
  --kb-warn: #9a5b1a;
  --kb-warn-soft: #f3e3c8;
  margin: 0;
  padding: 12px 4px 4px;
  color: var(--kb-ink);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.keberatan-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 14px;
}

.keberatan-toolbar-right {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.keberatan-search {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 260px;
  max-width: 420px;
  padding: 9px 12px;
  border-radius: 12px;
  background: var(--kb-surface);
  border: 1px solid var(--kb-line);
}

.keberatan-search-icon {
  display: inline-flex;
  color: var(--kb-muted);
}

.keberatan-search input {
  flex: 1;
  border: 0;
  outline: none;
  background: transparent;
  color: var(--kb-ink);
  font: inherit;
  font-size: 0.9rem;
}

.keberatan-search input::placeholder {
  color: #8a9b94;
}

.keberatan-chips {
  display: inline-flex;
  gap: 4px;
  padding: 3px;
  border-radius: 999px;
  background: var(--kb-surface);
  border: 1px solid var(--kb-line);
}

.keberatan-chip {
  border: 0;
  background: transparent;
  color: var(--kb-muted);
  border-radius: 999px;
  padding: 6px 12px;
  font: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.keberatan-chip.is-active {
  background: var(--kb-accent);
  color: #fff;
}

.keberatan-create-btn {
  border: 0;
  background: var(--kb-accent);
  color: #fff;
  border-radius: 10px;
  padding: 8px 14px;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease, opacity 0.15s ease;
}

.keberatan-create-btn:hover:not(:disabled) {
  background: #19684e;
}

.keberatan-create-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.keberatan-create-btn--empty {
  margin-top: 16px;
}

.keberatan-meta {
  display: flex;
  align-items: baseline;
  gap: 5px;
  padding: 6px 12px;
  border-radius: 999px;
  background: var(--kb-surface);
  border: 1px solid var(--kb-line);
}

.keberatan-count {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--kb-accent);
}

.keberatan-count-label {
  font-size: 0.75rem;
  color: var(--kb-muted);
  text-transform: lowercase;
}

.keberatan-table-wrap {
  overflow-x: auto;
  border-radius: 14px;
  border: 1px solid var(--kb-line);
}

.keberatan-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 780px;
}

.keberatan-table th {
  padding: 12px 14px;
  text-align: left;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6d8179;
  border-bottom: 1px solid var(--kb-line);
  background: var(--kb-surface);
  white-space: nowrap;
}

.keberatan-table td {
  padding: 12px 14px;
  vertical-align: top;
  border-bottom: 1px solid rgba(22, 50, 43, 0.06);
  font-size: 0.88rem;
}

.col-toggle {
  width: 36px;
  padding-left: 10px !important;
  padding-right: 2px !important;
}

.keberatan-row {
  cursor: pointer;
  transition: background 0.15s ease;
}

.keberatan-row:hover,
.keberatan-row.is-open {
  background: rgba(31, 122, 92, 0.04);
}

.keberatan-expand-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--kb-muted);
  cursor: pointer;
  transition: transform 0.2s ease, background 0.15s ease, color 0.15s ease;
}

.keberatan-row.is-open .keberatan-expand-btn {
  transform: rotate(180deg);
  color: var(--kb-accent);
  background: var(--kb-accent-soft);
}

.mono {
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
}

.cell-primary {
  display: block;
  font-weight: 600;
  color: var(--kb-ink);
  line-height: 1.35;
}

.cell-muted {
  color: var(--kb-muted);
  line-height: 1.4;
}

.cell-sub {
  display: block;
  margin-top: 3px;
  font-size: 0.76rem;
  color: #7b8f87;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.status-pill.is-done {
  color: var(--kb-accent);
  background: var(--kb-accent-soft);
}

.status-pill.is-pending {
  color: var(--kb-warn);
  background: var(--kb-warn-soft);
}

.keberatan-detail-row td {
  padding: 0 14px 12px;
  border-bottom: 1px solid rgba(22, 50, 43, 0.06);
  background: rgba(31, 122, 92, 0.03);
}

.keberatan-detail {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  padding: 10px 8px 2px 34px;
}

.detail-block {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.detail-label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #7b8f87;
}

.detail-value {
  font-weight: 600;
  color: var(--kb-ink);
  line-height: 1.35;
}

.detail-block--full {
  grid-column: 1 / -1;
}

.detail-value--text {
  font-weight: 500;
  white-space: pre-wrap;
  word-break: break-word;
}

.detail-sub {
  font-size: 0.78rem;
  color: var(--kb-muted);
}

.keberatan-skeleton {
  display: grid;
  gap: 8px;
  padding: 8px;
  border-radius: 14px;
  border: 1px solid var(--kb-line);
}

.keberatan-skeleton-row {
  display: grid;
  grid-template-columns: 1.4fr 1.2fr 1.2fr 0.5fr 0.6fr 0.7fr;
  gap: 10px;
  padding: 12px 10px;
}

.sk {
  display: block;
  height: 11px;
  border-radius: 999px;
  background: linear-gradient(90deg, #dfe8e3 25%, #eef4f0 50%, #dfe8e3 75%);
  background-size: 200% 100%;
  animation: kbShimmer 1.2s linear infinite;
}

.sk-xs { width: 48%; }
.sk-sm { width: 70%; }
.sk-md { width: 85%; }
.sk-lg { width: 92%; }

.keberatan-empty {
  text-align: center;
  padding: 44px 16px;
  border-radius: 14px;
  border: 1px dashed rgba(22, 50, 43, 0.18);
  background: var(--kb-surface);
}

.keberatan-empty-mark {
  width: 44px;
  height: 44px;
  margin: 0 auto 12px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(31, 122, 92, 0.2), rgba(61, 120, 160, 0.18));
}

.keberatan-empty-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
}

.keberatan-empty-text {
  margin: 6px 0 0;
  color: var(--kb-muted);
  font-size: 0.9rem;
}

.keberatan-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(14, 32, 28, 0.45);
}

.keberatan-modal {
  width: 100%;
  max-width: 440px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid var(--kb-line);
  box-shadow: 0 18px 48px rgba(14, 32, 28, 0.18);
}

.keberatan-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px 10px;
}

.keberatan-modal-header h2 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--kb-ink);
}

.keberatan-modal-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--kb-muted);
  cursor: pointer;
}

.keberatan-modal-close:hover:not(:disabled) {
  background: var(--kb-surface);
  color: var(--kb-ink);
}

.keberatan-modal-close:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.keberatan-modal-body {
  display: grid;
  gap: 14px;
  padding: 6px 18px 8px;
}

.keberatan-form-error {
  margin: 0;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f8e8e6;
  color: #8a2f2a;
  font-size: 0.85rem;
}

.keberatan-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.keberatan-field-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #7b8f87;
}

.keberatan-field select,
.keberatan-field textarea {
  width: 100%;
  border: 1px solid var(--kb-line);
  border-radius: 10px;
  background: var(--kb-surface);
  color: var(--kb-ink);
  padding: 10px 12px;
  font: inherit;
  font-size: 0.9rem;
  outline: none;
}

.keberatan-field textarea {
  resize: vertical;
  min-height: 96px;
  line-height: 1.45;
}

.keberatan-field textarea::placeholder {
  color: #8a9b94;
}

.keberatan-field select:focus,
.keberatan-field textarea:focus {
  border-color: rgba(31, 122, 92, 0.45);
  background: #fff;
}

.keberatan-field select:disabled,
.keberatan-field textarea:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.keberatan-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 18px 16px;
}

.keberatan-btn-secondary {
  border: 1px solid var(--kb-line);
  background: var(--kb-surface);
  color: var(--kb-ink);
  border-radius: 10px;
  padding: 8px 14px;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
}

.keberatan-btn-secondary:hover:not(:disabled) {
  background: #fff;
}

.keberatan-btn-secondary:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.keberatan-pager {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
  color: var(--kb-muted);
  font-size: 0.82rem;
}

.keberatan-pager button {
  border: 1px solid var(--kb-line);
  background: var(--kb-surface);
  color: var(--kb-ink);
  border-radius: 10px;
  padding: 7px 11px;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, opacity 0.15s ease;
}

.keberatan-pager button:hover:not(:disabled) {
  background: #fff;
}

.keberatan-pager button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

@keyframes kbShimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media (max-width: 900px) {
  .keberatan-toolbar {
    align-items: stretch;
  }

  .keberatan-toolbar-right {
    width: 100%;
    justify-content: space-between;
  }

  .keberatan-detail {
    grid-template-columns: 1fr;
    padding-left: 8px;
  }
}
</style>
